import { useState, useEffect } from 'react';
import { ArrowRight, X, MessageSquare } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';

interface StickyProps {
  currentSection: string;
}

export default function StickyCTA({ currentSection }: StickyProps) {
  const [isVisible, setIsVisible] = useState(false);
  const [isDismissed, setIsDismissed] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);
  const { t, language } = useLanguage();

  useEffect(() => {
    const dismissed = sessionStorage.getItem('stickyCTADismissed');
    if (dismissed) {
      setIsDismissed(true);
      return;
    }

    const timer = setTimeout(() => {
      setIsVisible(true);
    }, 12000);

    return () => clearTimeout(timer);
  }, []);

  const getContextualText = () => {
    switch (currentSection) {
      case 'services':
        return t('sticky.services');
      case 'process':
        return t('sticky.process');
      case 'references':
        return t('sticky.references');
      case 'contact':
        return null;
      default:
        return t('sticky.default');
    }
  };

  const handleDismiss = () => {
    setIsDismissed(true);
    setIsVisible(false);
    sessionStorage.setItem('stickyCTADismissed', 'true');
  };

  const handleClick = () => {
    const formSection = document.getElementById('anfrage') || document.getElementById('contact');
    if (formSection) {
      formSection.scrollIntoView({ behavior: 'smooth' });
    }
    handleDismiss();
  };

  const text = getContextualText();

  if (!text || isDismissed || currentSection === 'contact') {
    return null;
  }

  return (
    <div
      className={`fixed bottom-6 right-6 z-50 transition-all duration-500 ${
        isVisible ? 'translate-y-0 opacity-100' : 'translate-y-4 opacity-0 pointer-events-none'
      }`}
    >
      {isExpanded ? (
        <div className="bg-white dark:bg-gray-800 rounded-card shadow-2xl border border-line dark:border-gray-700 p-5 w-[min(18rem,calc(100vw-3rem))]">
          <div className="flex items-start justify-between mb-3">
            <div className="w-8 h-8 rounded-full bg-primary flex items-center justify-center flex-shrink-0">
              <MessageSquare className="w-4 h-4 text-white" />
            </div>
            <button
              onClick={handleDismiss}
              className="p-1 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 rounded-lg transition-colors"
              aria-label={t('sticky.close')}
            >
              <X className="w-4 h-4" />
            </button>
          </div>
          <p className="text-sm font-semibold text-gray-900 dark:text-white mb-4 leading-snug">
            {text}
          </p>
          <button
            onClick={handleClick}
            className="w-full px-4 py-2.5 bg-primary text-white rounded-full text-sm font-bold hover:bg-primary-hover transition-all flex items-center justify-center gap-2 group shadow-md"
          >
            {t('sticky.contact')}
            <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
          </button>
        </div>
      ) : (
        <button
          onClick={() => setIsExpanded(true)}
          className="w-14 h-14 rounded-full bg-primary hover:bg-primary-hover text-white shadow-lg hover:shadow-xl transition-all flex items-center justify-center group relative"
          aria-label={language === 'de' ? 'Beratung anfragen' : 'Request consultation'}
        >
          <MessageSquare className="w-6 h-6" />
          <span className="absolute -top-1 -right-1 w-3 h-3 bg-green-400 rounded-full border-2 border-white"></span>
        </button>
      )}
    </div>
  );
}
